import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchQuoteById, deleteQuoteComment } from '../../context/CommonContex';
import HoverPO from '../HoverPO';
import { getCommentId, isTempCommentId, toStoredComment } from '../../utils/commentText';
import './Quotes.css';

const formatPartNumber = (value) => {
    const partNumber = String(value || '');
    if (!partNumber || partNumber === '-') return '-';
    return partNumber.length >= 3 ? partNumber.substring(3) : partNumber;
};

const QuoteDetailsPage = () => {
    const { quoteId } = useParams();
    const navigate = useNavigate();

    const [quoteData, setQuoteData] = useState(null);
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [partLinkDropdown, setPartLinkDropdown] = useState({
        open: false,
        top: 0,
        left: 0,
        partId: null,
    });

    useEffect(() => {
        const loadQuote = async () => {
            if (!quoteId) {
                setError('No quote id provided');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                const quote = await fetchQuoteById(quoteId);

                if (!quote) {
                    setError(`Quote ${quoteId} not found`);
                    setLoading(false);
                    return;
                }

                setQuoteData(quote);
                setComments(Array.isArray(quote.comment) ? quote.comment : []);
                setLoading(false);
            } catch (err) {
                console.error('Error fetching quote details:', err);
                setError(err.message || 'Failed to load quote details');
                setLoading(false);
            }
        };

        loadQuote();
    }, [quoteId]);

    const handlePartLinkDropdownToggle = (line, event) => {
        event.stopPropagation();
        const partId = line?.part?.id;
        if (!partId) return;

        const buttonRect = event.currentTarget.getBoundingClientRect();
        const isSamePartOpen = partLinkDropdown.open && partLinkDropdown.partId === partId;

        if (isSamePartOpen) {
            setPartLinkDropdown({ open: false, top: 0, left: 0, partId: null });
            return;
        }

        const menuHeight = 140;
        const menuWidth = 200;
        const spaceBelow = window.innerHeight - buttonRect.bottom;

        setPartLinkDropdown({
            open: true,
            top: spaceBelow < menuHeight
                ? Math.max(8, buttonRect.top - menuHeight)
                : buttonRect.bottom + 4,
            left: Math.min(buttonRect.left, window.innerWidth - menuWidth - 8),
            partId,
        });
    };

    useEffect(() => {
        if (!partLinkDropdown.open) return;

        const closeDropdown = (event) => {
            const target = event?.target;
            if (target?.closest?.('.quote-details-part-link-trigger') || target?.closest?.('.part-link-dropdown-menu')) {
                return;
            }
            setPartLinkDropdown({ open: false, top: 0, left: 0, partId: null });
        };

        document.addEventListener('mousedown', closeDropdown);
        window.addEventListener('scroll', closeDropdown, true);
        window.addEventListener('resize', closeDropdown);

        return () => {
            document.removeEventListener('mousedown', closeDropdown);
            window.removeEventListener('scroll', closeDropdown, true);
            window.removeEventListener('resize', closeDropdown);
        };
    }, [partLinkDropdown.open]);

    const handleClose = () => {
        navigate('/quotes');
    };

    const handleDeleteComment = async (commentId) => {
        const target = comments.find((c) => String(getCommentId(c)) === String(commentId) || String(c?.id) === String(commentId));
        const backendId = getCommentId(target) || commentId;
        setComments(prev => prev.filter((c) => String(getCommentId(c)) !== String(commentId) && String(getCommentId(c)) !== String(backendId)));
        try {
            if (!isTempCommentId(backendId) && quoteId) {
                await deleteQuoteComment(backendId, quoteId);
            }
        } catch (error) {
            console.error('Error deleting quote comment from backend:', error);
        }
    };

    const handleResolveThread = () => {
        setComments(prev => prev.map(c => ({ ...c, status: 'resolved' })));
    };

    const handleAddComment = (newComment, status) => {
        const comment = toStoredComment(newComment, status || 'pending', {
            lastEditedBy: localStorage.getItem('userEmail') || 'You',
        });
        setComments(prev => {
            const filtered = prev.filter(c => String(c?.id) !== String(comment.id));
            return [...filtered, comment];
        });
    };

    if (loading) {
        return (
            <div className="container-fluid vh-100 d-flex justify-content-center align-items-center">
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <div className="mt-3">
                        <h5>Loading Quote Details...</h5>
                        <p className="text-muted">Quote #{quoteId}</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container-fluid vh-100 d-flex justify-content-center align-items-center">
                <div className="text-center">
                    <div className="alert alert-danger" role="alert">
                        <i className="fa fa-exclamation-triangle" style={{ fontSize: '48px', marginBottom: '20px' }}></i>
                        <h4 className="alert-heading">Error Loading Quote</h4>
                        <p>{error}</p>
                        <hr />
                        <button className="btn btn-primary" onClick={handleClose}>
                            <i className="fa fa-arrow-left me-2"></i>
                            Back to Quotes
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!quoteData) {
        return (
            <div className="container-fluid vh-100 d-flex justify-content-center align-items-center">
                <div className="text-center">
                    <div className="alert alert-warning" role="alert">
                        <h4 className="alert-heading">Quote Not Found</h4>
                        <p>Quote #{quoteId} could not be found in the system.</p>
                        <hr />
                        <button className="btn btn-primary" onClick={handleClose}>
                            <i className="fa fa-arrow-left me-2"></i>
                            Back to Quotes
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid quote-details-page" style={{ padding: '20px', minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
            {/* Header with back button and quote info */}
            <div className="row mb-3">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-3">
                            <button
                                className="btn btn-outline-primary"
                                onClick={handleClose}
                                title="Back to Quotes"
                            >
                                <i className="fa fa-arrow-left me-2"></i>
                                Back to Quotes
                            </button>
                            <h3 className="mb-0">
                                Quote Details - #{quoteId}
                            </h3>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <a
                                href={`https://gigaflightinc.cetecerp.com/quote/${quoteId}/view`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-outline-secondary"
                            >
                                <i className="fa fa-external-link me-2"></i>
                                View in Cetec
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            {/* Quote Details Content - mirrors the "eye icon -> Modal Mode" inventory view from Quotes.js */}
            <div className="row">
                <div className="col-12">
                    <div className="box-body border bg-white rounded-2 p-4">
                        <div className="row mb-3">
                            <div className="col-md-6">
                                <h5>
                                    Customer:{' '}
                                    {quoteData.customer_id ? (
                                        <a
                                            href={`https://gigaflightinc.cetecerp.com/customer/${quoteData.customer_id}/view`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ textDecoration: 'none' }}
                                        >
                                            {quoteData.customer_name}
                                        </a>
                                    ) : (
                                        quoteData.customer_name
                                    )}
                                </h5>
                            </div>
                            <div className="col-md-6">
                                <p className="text-end">Status: <span className="badge bg-info">{quoteData.status}</span></p>
                            </div>
                        </div>

                        <hr />
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="mb-0">Quote Lines with Inventory Details:</h6>
                            <HoverPO
                                comments={comments}
                                quoteId={quoteId}
                                orderNumber={quoteId}
                                customerName={quoteData.customer_name}
                                customerId={quoteData.customer_id}
                                sourcePage="quotes"
                                onDeleteComment={handleDeleteComment}
                                onResolveThread={handleResolveThread}
                                onAddComment={handleAddComment}
                            >
                                <button
                                    type="button"
                                    title="Click to view/add notes"
                                    className="btn btn-light"
                                    style={{ padding: '6px', borderRadius: '50%', border: '1px solid #ccc' }}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" height="16" viewBox="0 0 512 512">
                                        <path
                                            fill="currentColor"
                                            d="M344 0L488 0c13.3 0 24 10.7 24 24l0 144c0 9.7-5.8 18.5-14.8 22.2s-19.3 1.7-26.2-5.2l-39-39-87 87c-9.4 9.4-24.6 9.4-33.9 0l-32-32c-9.4-9.4-9.4-24.6 0-33.9l87-87L327 41c-6.9-6.9-8.9-17.2-5.2-26.2S334.3 0 344 0zM168 512L24 512c-13.3 0-24-10.7-24-24L0 344c0-9.7 5.8-18.5 14.8-22.2s19.3-1.7 26.2 5.2l39 39 87-87c9.4-9.4 24.6-9.4 33.9 0l32 32c9.4 9.4 9.4 24.6 0 33.9l-87 87 39 39c6.9 6.9 8.9 17.2 5.2 26.2s-12.5 14.8-22.2 14.8z"
                                        />
                                    </svg>
                                </button>
                            </HoverPO>
                        </div>

                        {quoteData.lines?.length > 0 ? (
                            <div className="table-responsive">
                                <table className="table gf-table table-bordered table-striped compact-data-table">
                                    <thead className="table-dark">
                                        <tr>
                                            <th>Line #</th>
                                            <th>Part No</th>
                                            <th>QOO</th>
                                            <th>QR</th>
                                            <th>QOH</th>
                                            <th>Available</th>
                                            <th>Quantity</th>
                                            <th>Request Resale</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {quoteData.lines.map((line, idx) => {
                                            const qoh = Number(line.part?.qoh_cache) || 0;
                                            const qr = Number(line.part?.qr_cache) || 0;
                                            const qoo = Number(line.part?.qoo_cache) || 0;
                                            const qtyInHand = qoh - qr;

                                            const hasPartInfo = line.part && (line.part.qoh_cache !== undefined || line.part.qoo_cache !== undefined || line.part.qr_cache !== undefined);

                                            return (
                                                <tr key={idx}>
                                                    <td>{idx + 1}</td>
                                                    <td className="quote-details-part-cell">
                                                        <div className="d-flex align-items-center gap-2">
                                                            <span className="quote-details-part-number">{formatPartNumber(line.prcpart || line.part?.part_number)}</span>
                                                            {line.part?.id && (
                                                                <button
                                                                    className="btn btn-sm btn-outline-secondary py-0 px-1 quote-details-part-link-trigger"
                                                                    type="button"
                                                                    aria-expanded={partLinkDropdown.open && partLinkDropdown.partId === line.part.id}
                                                                    title="View Part Links"
                                                                    onClick={(event) => handlePartLinkDropdownToggle(line, event)}
                                                                >
                                                                    <i className="fa fa-eye" aria-hidden="true"></i>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        {hasPartInfo ? (
                                                            <>
                                                                {qoo}
                                                                {qoo !== 0 && line.part?.id && (
                                                                    <a href={`https://gigaflightinc.cetecerp.com/part/${line.part.id}/pos`} target="_blank" rel="noreferrer" title="View Purchase Orders" className="ms-2">
                                                                        <i className="fa fa-external-link" aria-hidden="true"></i>
                                                                    </a>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <span className="text-muted">N/A</span>
                                                        )}
                                                    </td>
                                                    <td className="text-center">{hasPartInfo ? qr : <span className="text-muted">N/A</span>}</td>
                                                    <td className="text-center">{hasPartInfo ? qoh : <span className="text-muted">N/A</span>}</td>
                                                    <td className="text-center">
                                                        {hasPartInfo ? (
                                                            <span className={qtyInHand < 0 ? 'text-danger fw-bold' : ''}>
                                                                {qtyInHand}
                                                            </span>
                                                        ) : (
                                                            <span className="text-muted">N/A</span>
                                                        )}
                                                    </td>
                                                    <td className="text-center">{line.orig_qty != null ? Number(line.orig_qty).toFixed(3) : '-'}</td>
                                                    <td className="text-center">{line.request_resale != null ? Number(line.request_resale).toFixed(2) : '-'}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p className="text-center text-muted py-3">No line items available.</p>
                        )}

                        {comments.length > 0 && (
                            <>
                                <hr />
                                <h6>Comments:</h6>
                                <ul className="list-unstyled">
                                    {comments.map((c, idx) => (
                                        <li key={c.id || idx} className="border rounded p-2 mb-2">
                                            <div className="d-flex justify-content-between">
                                                <strong>{c.lastEditedBy || 'Unknown'}</strong>
                                                <span className="text-muted small">{c.dateStr} {c.timeStr}</span>
                                            </div>
                                            <div>{c.comment}</div>
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </div>
                </div>
            </div>
            {partLinkDropdown.open && partLinkDropdown.partId && createPortal(
                <div
                    className="part-link-dropdown-menu"
                    style={{
                        position: 'fixed',
                        top: `${partLinkDropdown.top}px`,
                        left: `${partLinkDropdown.left}px`,
                        minWidth: '180px',
                        zIndex: 1000005,
                        backgroundColor: 'white',
                        border: '1px solid #dee2e6',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        padding: '4px 0'
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                >
                    <a
                        className="dropdown-item d-flex align-items-center"
                        href={`https://gigaflightinc.cetecerp.com/part/${partLinkDropdown.partId}/inventory`}
                        target="_blank"
                        rel="noreferrer"
                        style={{ padding: '8px 14px', textDecoration: 'none', color: '#212529' }}
                    >
                        <i className="fa fa-boxes me-2" aria-hidden="true"></i>Inventory
                    </a>
                    <a
                        className="dropdown-item d-flex align-items-center"
                        href={`https://gigaflightinc.cetecerp.com/part/${partLinkDropdown.partId}/waterfall`}
                        target="_blank"
                        rel="noreferrer"
                        style={{ padding: '8px 14px', textDecoration: 'none', color: '#212529' }}
                    >
                        <i className="fa fa-chart-line me-2" aria-hidden="true"></i>Waterfall
                    </a>
                    <a
                        className="dropdown-item d-flex align-items-center"
                        href={`https://gigaflightinc.cetecerp.com/part/${partLinkDropdown.partId}/pos`}
                        target="_blank"
                        rel="noreferrer"
                        style={{ padding: '8px 14px', textDecoration: 'none', color: '#212529' }}
                    >
                        <i className="fa fa-shopping-cart me-2" aria-hidden="true"></i>Purchase Orders
                    </a>
                </div>,
                document.body
            )}
        </div>
    );
};

export default QuoteDetailsPage;
